import { useEffect, useState } from "react"
import { type AttendanceOperation } from "@/domain/features/company/v2/entities/attendance_operation/attendance_operation"
import { AttendanceOperationRepository } from "@/domain/features/company/v2/repositories/attendance_operation_repository"
import { getFirebaseFirestoreInfra } from "@/infrastructure/firebase_firestore/client"

/**
 * 出勤操作を管理するカスタムフック。
 *
 * @param initialAttendanceOperation - 出勤操作の初期状態。もし指定されていれば、その状態から開始します。
 * @param companyId - 会社のID。出勤操作に関連する会社の識別子。
 * @param userId - ユーザーのID。出勤操作に関連するユーザーの識別子。
 * @returns 現在の出勤操作の状態。Firestoreから最新のデータをリアルタイムで取得して返します。
 *
 * このフックは、指定された `userId` と `companyId` に基づいてFirestoreのドキュメントを監視し、
 * 変更があった場合に出勤操作の状態をリアルタイムで更新します。例えば、出勤操作の開始時刻や終了時刻の変更などが反映されます。
 *
 * 主に出勤操作のデータを管理するために使用され、次のような操作が行われます：
 * - 出勤操作の状態（開始時刻、終了時刻など）を取得。
 * - 出勤操作に関するデータが変更された場合に自動的に更新。
 *
 * このフックは、`AttendanceOperationRepository` を使用してFirestoreのドキュメントを監視し、
 * `onSnapshot` メソッドによってリアルタイムのデータ更新を受け取ります。
 *
 * 詳細な仕様については、以下のリンクを参照してください。
 *
 * @see {@link https://www.figma.com/board/yl20b4ZpVPTNndYXKcrQ3l/Firestore?node-id=69-2512&t=pOLcwTrfQ46Tr9Z5-4}
 */
export const useAttendanceOperation = (
  initialAttendanceOperation: AttendanceOperation | undefined,
  companyId: string,
  userId: string
) => {
  const [attendanceOperation, setAttendanceOperation] = useState<
    AttendanceOperation | undefined
  >(initialAttendanceOperation)

  useEffect(() => {
    const firestore = getFirebaseFirestoreInfra()

    if (userId == null) {
      return () => {}
    }
    const unsubscribe = new AttendanceOperationRepository(firestore).onSnapshot(
      { userId, companyId },
      (data) => {
        setAttendanceOperation(data)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [companyId, userId])

  return attendanceOperation
}
